/**
 * Every flashpoint a Flashpoint Race round can pick (combat-log area id, name,
 * loading screen), for the round form and for naming stored selections.
 *
 * Fetched once per page load and shared by every caller (the list changes only
 * when the platform publishes a release). A failed load is not cached, so the
 * next mount retries.
 */

import { useEffect, useMemo, useState } from 'react';
import type { PlatformFlashpointOption } from '@swtorbit/shared-socket-events';
import { fetchPlatformFlashpoints } from '../utils/platformApi';

let shared: Promise<PlatformFlashpointOption[]> | null = null;

function loadFlashpoints(): Promise<PlatformFlashpointOption[]> {
  shared ??= fetchPlatformFlashpoints()
    .then((r) => r.flashpoints)
    .catch((error: unknown) => {
      shared = null;
      throw error;
    });
  return shared;
}

export interface UseFlashpointCatalogue {
  flashpoints: PlatformFlashpointOption[];
  /** Keyed by flashpoint id (FlashpointSelection.flashpointId). */
  byId: Map<string, PlatformFlashpointOption>;
  loading: boolean;
  error: string | null;
}

export function useFlashpointCatalogue(): UseFlashpointCatalogue {
  const [flashpoints, setFlashpoints] = useState<PlatformFlashpointOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    loadFlashpoints()
      .then((f) => { if (live) { setFlashpoints(f); setError(null); } })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Failed to load flashpoints'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const byId = useMemo(() => new Map(flashpoints.map((f) => [f.id, f])), [flashpoints]);

  return { flashpoints, byId, loading, error };
}
